import Cookies from 'js-cookie'

import { getBookingFlowFromSearchParams } from '@/utils/bookingFlow'
import { getSct } from '@/utils/getSct'
import { AppMode, Country } from '../models/app'

import { BookingFlow } from './bookingAvailabilitySlice'
import { resetAllState, setupByUrl } from './commonActions'
import { createAppSlice } from './createAppSlice'

const COUNTRY_COOKIE = 'spiagge-country'

export enum AppChannel {
  YOUR_BEACH = 'yb',
  APP_YOUR_BEACH = 'app-yb',
  KIOSK = 'kiosk',
  WIDGET = 'widget',
}

export type AppSliceState = {
  bookingFlow: null | BookingFlow
  channel: AppChannel | string
  country: Country
  mode: AppMode
  sct: number[] | null
}

const initialState: AppSliceState = {
  bookingFlow: null,
  // channel is a free text string, so it can be anything
  // it's used to track the source of the user
  channel: 'yb',
  country: Country.IT,
  // mode should be the kind of app the user is using
  mode: AppMode.WIDGET,
  sct: null,
}

function getAppMode(url: URL): AppMode {
  const { host } = url

  if (host.startsWith('app.') || host.startsWith('new-app.')) {
    return AppMode.APP
  }

  if (host.startsWith('booking.') || host.startsWith('new-booking.')) {
    return AppMode.WEBSITE
  }

  if (host.startsWith('kiosk.') || host.startsWith('new-kiosk.')) {
    return AppMode.KIOSK
  }

  if (host.startsWith('widget.') || host.startsWith('new-widget.')) {
    return AppMode.WIDGET
  }

  return AppMode.WIDGET
}

export const appSlice = createAppSlice({
  extraReducers(builder) {
    builder.addCase(resetAllState, () => initialState)
    builder.addCase(setupByUrl, (state, action) => {
      const url = new URL(action.payload)
      const searchParamChannel = url.searchParams.get('ch')
      const appMode = getAppMode(url)
      const bookingFlow = getBookingFlowFromSearchParams(url.searchParams)

      state.bookingFlow = bookingFlow

      const sct = getSct(action.payload)
      if (sct !== null) {
        state.sct = sct
      }

      if (searchParamChannel === 'yb') {
        state.channel = 'yb'
        state.mode = AppMode.WEBSITE
      } else if (searchParamChannel === 'app-yb') {
        state.channel = 'app-yb'
        state.mode = AppMode.APP
      } else if (searchParamChannel === 'kiosk') {
        state.channel = 'kiosk'
        state.mode = AppMode.KIOSK
      } else if (!searchParamChannel) {
        if (appMode === AppMode.WEBSITE) {
          state.channel = 'yb'
          state.mode = AppMode.WEBSITE
        } else if (appMode === AppMode.WIDGET) {
          state.channel = 'widget'
          state.mode = AppMode.WIDGET
        } else if (appMode === AppMode.KIOSK) {
          state.channel = 'kiosk'
          state.mode = AppMode.KIOSK
        }
      } else {
        if (appMode === AppMode.WEBSITE) {
          state.channel = 'yb'
          state.mode = AppMode.WEBSITE
        } else {
          state.channel = searchParamChannel
          state.mode = appMode
        }
      }
      const country = url.searchParams.get('country')
      if (country !== null) {
        switch (country) {
          case 'fr':
            state.country = Country.FR
            break

          default:
            state.country = Country.IT
            break
        }
      } else {
        // check cookies
        const countryCookie = Cookies.get(COUNTRY_COOKIE)
        if (typeof countryCookie === 'string') {
          state.country = countryCookie as Country
        }
      }

      Cookies.set(COUNTRY_COOKIE, state.country)
    })
  },
  initialState,
  name: 'app',
  reducers: {},
  selectors: {
    bookingFlow: (state) => state.bookingFlow,
    channel: (state) => state.channel,
    country: (state) => state.country,
    isAppMode: (state) => state.mode === AppMode.APP,
    isKiosk: (state) => state.mode === AppMode.KIOSK,
    isWebsiteMode: (state) => state.mode === AppMode.WEBSITE,
    isWidgetMode: (state) => state.mode === AppMode.WIDGET,
    mode: (state) => state.mode,
    sct: (state) => state.sct,
  },
})
